import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Star, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { CommunityEvent } from "@/types/rider";
import { EventCard } from "./EventCard";

interface VehicleOption {
  id: string;
  name: string;
  capacity: number;
  pricing: { [key: string]: number };
  icon: string;
}

interface MobileEventSelectorV2Props {
  events: CommunityEvent[];
  selectedEventId: string | null;
  selectedVehicleId: string | null;
  vehicleOptions: VehicleOption[];
  activeIndex: number;
  onEventSelect: (eventId: string, index: number) => void;
  onVehicleSelect: (vehicleId: string) => void;
  onContinue: (selectedAddOns?: any[], totalPrice?: number) => void;
  onManualEntry: () => void;
  onPrevCard?: () => void;
  onNextCard?: () => void;
  onBackToVehicles: () => void;
}

export const MobileEventSelectorV2 = ({
  events,
  selectedEventId,
  selectedVehicleId,
  vehicleOptions,
  activeIndex,
  onEventSelect,
  onVehicleSelect,
  onContinue,
  onManualEntry,
  onPrevCard,
  onNextCard,
  onBackToVehicles,
}: MobileEventSelectorV2Props) => {
  const navigate = useNavigate();
  const selectedEvent = events.find(event => event.id === selectedEventId);
  const selectedVehicle = vehicleOptions.find(vehicle => vehicle.id === selectedVehicleId);
  
  // Add-ons state for mobile
  const [addOns, setAddOns] = useState([
    {
      id: '1',
      description: 'Ship Wrek @ The Grand After Party',
      price: 0,
      enabled: false
    },
    {
      id: '2', 
      description: 'DJ Diesel @ Big Night Live After Party',
      price: 0,
      enabled: false
    },
    {
      id: '3',
      description: 'Professional Photography Service',
      price: 0,
      enabled: false
    }
  ]);

  // Get base price from selected vehicle and event duration
  // Extract duration from startTime (e.g., "7 Hour Reservation | Round Trip" → "7")
  const durationMatch = selectedEvent?.startTime?.match(/(\d+)\s*Hour/);
  const eventDuration = durationMatch ? durationMatch[1] : '4';
  const basePrice = selectedVehicle?.pricing[eventDuration] || 0;
  const totalPrice = basePrice + addOns.filter(addon => addon.enabled && addon.price > 0).reduce((sum, addon) => sum + addon.price, 0);

  const toggleAddOn = (id: string) => {
    setAddOns(prev => prev.map(addon => 
      addon.id === id ? { ...addon, enabled: !addon.enabled } : addon
    ));
  };

  return (
    <div className="md:hidden bg-white text-black pb-24">
      <div className="flex justify-between items-center mb-8">
        <button 
          onClick={() => navigate('/')}
          className="flex items-center text-black hover:text-gray-700 transition-colors"
        >
          <ArrowLeft className="mr-1 h-5 w-5" />
          <span>Book A Custom Trip</span>
        </button>
        <div></div>
      </div>

      <h1 className="text-3xl font-bold text-center mb-4 text-black">Book A Party Bus To</h1>
      
      <AnimatePresence mode="wait">
        {!selectedEventId ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="flex flex-col items-center"
          >
            <div className="relative w-full h-[450px] mb-8 flex items-center justify-center">
              {events.map((event, index) => {
                const offset = index - activeIndex;
                const isActive = index === activeIndex;
                
                if (Math.abs(offset) > 2) return null;
                
                return (
                  <motion.div
                    key={event.id}
                    className="absolute w-[85%] max-w-[330px]"
                    style={{
                      zIndex: events.length - Math.abs(offset),
                    }}
                    animate={{
                      y: offset * 10,
                      x: offset * 10,
                      rotate: offset * 3,
                      scale: 1 - Math.abs(offset) * 0.08,
                      opacity: 1 - Math.abs(offset) * 0.15,
                    }}
                    transition={{
                      type: "spring",
                      stiffness: 200,
                      damping: 20
                    }}
                    onClick={() => onEventSelect(event.id, index)}
                  >
                    <EventCard
                      event={event}
                      isSelected={false}
                      onClick={() => {}}
                    />
                  </motion.div>
                );
              })}
            </div>
            
            {/* Navigation buttons */}
            <motion.div 
              className="flex space-x-4 mb-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
            >
              <button 
                className="bg-black text-white p-3 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={onPrevCard}
                disabled={activeIndex === 0}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button 
                className="bg-black text-white p-3 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={onNextCard}
                disabled={activeIndex === events.length - 1}
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </motion.div>
            
            <motion.p 
              className="text-xl text-center mb-6 text-black font-semibold"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              Select An Event To See Vehicle Options
            </motion.p>
            <motion.div
              className="text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <p className="text-gray-600 mb-2">Or</p>
              <button 
                onClick={() => navigate('/')}
                className="text-gray-600 underline hover:text-black transition-colors"
              >
                Book A Custom Trip
              </button>
            </motion.div>
          </motion.div>
        ) : selectedEventId && !selectedVehicleId ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="flex flex-col items-center px-4"
          >
            {selectedEvent && (
              <>
                <motion.div 
                  className="w-full rounded-2xl overflow-hidden mb-4 h-[300px]"
                  layoutId={`event-card-${selectedEvent.id}`}
                >
                  <EventCard
                    event={selectedEvent}
                    isSelected={false}
                    onClick={() => {}}
                  />
                </motion.div>
                
                <div className="w-full bg-white rounded-xl p-6 shadow-lg">
                  <h2 className="text-2xl font-bold mb-2 text-black">{selectedEvent.name}</h2>
                  <p className="text-gray-600 mb-6">{selectedEvent.startTime}</p>
                  
                  <h3 className="text-xl font-semibold mb-4 text-black">Choose Your Vehicle</h3>
                  <div className="grid grid-cols-2 gap-3 mb-6">
                    {vehicleOptions.map((vehicle) => {
                      const price = vehicle.pricing[eventDuration];
                      
                      return (
                        <motion.div
                          key={vehicle.id}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <div 
                            className="cursor-pointer transition-all duration-200 hover:shadow-md border border-gray-200 bg-white rounded-lg p-3"
                            onClick={() => onVehicleSelect(vehicle.id)}
                          >
                            <div className="text-center">
                              <div className="text-2xl mb-2">{vehicle.icon}</div>
                              <h4 className="text-sm font-semibold mb-1 text-black">{vehicle.name}</h4>
                              <p className="text-xs text-gray-600 mb-2">Capacity {vehicle.capacity}</p>
                              <div className="text-lg font-bold text-black">${price}</div>
                              <p className="text-xs text-gray-500">{eventDuration} Hour Reservation</p>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </motion.div>
        ) : selectedEventId && selectedVehicleId ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="flex flex-col items-center"
          >
            {selectedEvent && (
              <>
                <div className="flex items-center mb-4">
                  <button 
                    onClick={onBackToVehicles}
                    className="flex items-center text-black hover:text-gray-700 transition-colors mr-4"
                  >
                    <ArrowLeft className="mr-1 h-4 w-4" />
                    <span className="text-sm">Back</span>
                  </button>
                </div>
                
                <motion.div 
                  className="w-full rounded-2xl overflow-hidden mb-4 h-[300px]"
                  layoutId={`event-card-${selectedEvent.id}`}
                >
                  <EventCard
                    event={selectedEvent}
                    isSelected={false}
                    onClick={() => {}}
                  />
                </motion.div>
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="w-full px-4"
                >
                  <h2 className="text-2xl font-bold mb-2 text-black">{selectedEvent.name}</h2>
                  <p className="text-gray-600 mb-2">{selectedEvent.startTime}</p>
                  <p className="text-gray-600 mb-6">{selectedVehicle?.name} - Capacity {selectedVehicle?.capacity}</p>
                  
                   <div className="flex justify-between items-center mb-6 border border-gray-200 rounded-lg p-4 bg-white">
                     <div>
                       <p className="text-2xl font-bold text-black">${totalPrice}</p>
                       <p className="text-gray-500">Total Price</p>
                     </div>
                     <div className="flex flex-col items-end">
                       <p className="text-2xl font-bold text-black">${Math.round(totalPrice / (selectedVehicle?.capacity || 1))}</p>
                       <p className="text-gray-500">Per Person</p>
                     </div>
                   </div>
                   
                   {/* Add-ons Section for Mobile - Only show for long duration events (7+ hours) */}
                   {parseInt(eventDuration) >= 7 && (
                     <div className="mb-6 space-y-3">
                       {selectedEvent.date?.includes('Friday') ? (
                         <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                           <div className="flex-1">
                             <p className="text-black font-medium text-sm">Take Me To Ship Wrek @ The Grand After Party</p>
                             <p className="text-gray-600 text-xs">Free - $0</p>
                           </div>
                           <label className="relative inline-flex items-center cursor-pointer">
                             <input
                               type="checkbox"
                               className="sr-only peer"
                               checked={addOns[0]?.enabled || false}
                               onChange={() => toggleAddOn('1')}
                             />
                             <div className="w-10 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[1px] after:left-[1px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-500"></div>
                           </label>
                         </div>
                       ) : selectedEvent.date?.includes('Saturday') ? (
                         <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                           <div className="flex-1">
                             <p className="text-black font-medium text-sm">Take Me To DJ Diesel @ Big Night Live After Party</p>
                             <p className="text-gray-600 text-xs">Free - $0</p>
                           </div>
                           <label className="relative inline-flex items-center cursor-pointer">
                             <input
                               type="checkbox"
                               className="sr-only peer"
                               checked={addOns[1]?.enabled || false}
                               onChange={() => toggleAddOn('2')}
                             />
                             <div className="w-10 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[1px] after:left-[1px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-500"></div>
                           </label>
                         </div>
                       ) : null}
                     </div>
                   )}
                   
                   <div className="flex justify-center pb-8">
                     <Button 
                       className="w-full bg-white text-black border border-black hover:bg-black hover:text-white rounded-full py-6"
                       onClick={() => onContinue(addOns.filter(addon => addon.enabled), totalPrice)}
                     >
                       Book Today 🚌
                     </Button>
                   </div>
                </motion.div>
              </>
            )}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
};